import { convertFromTableData } from '../../hooks/use-excel-table';
import {
  planToTableRow,
  splitRowInto3Lines,
  merge3LinesIntoRow,
} from '../../utils/plan-field-converters';

import type { CreatePlanRequest } from '../../types/plan.types';
import type { SearchOption } from '@/types/search';

/**
 * Convert plans array to 3-line table data format
 * Uses shared utilities to avoid code duplication
 */
export const convertTo3LineTableData = (
  plans: CreatePlanRequest[],
  plantOptions: SearchOption[] = [],
  witnessCompanyOptions: SearchOption[] = []
): unknown[][] => {
  return plans.flatMap((plan, planIndex) => {
    const sttNumber = planIndex + 1;

    // Reuse shared conversion logic to get 1-line format first
    const oneLineRow = planToTableRow(plan, plantOptions, witnessCompanyOptions);

    // Split into 3 lines using shared utility
    const { line1, line2, line3 } = splitRowInto3Lines(oneLineRow, sttNumber);

    return [line1, line2, line3];
  });
};

/**
 * Convert 3-line table data directly to 1-line table data format
 * Preserves string values to prevent NaN conversion during layout switching
 * This is used when switching layouts, not when submitting
 * Uses shared utilities to avoid code duplication
 */
export const convert3LineTo1LineTableDataPreserveString = (
  tableData: unknown[][],
  _plantOptions: SearchOption[] = [],
  _witnessCompanyOptions: SearchOption[] = []
): unknown[][] => {
  const rowsPerPlan = 3; // Fixed: 3 lines per plan
  const result: unknown[][] = [];

  for (let i = 0; i < tableData.length; i += rowsPerPlan) {
    const line1Row = tableData[i];
    const line2Row = tableData[i + 1];
    const line3Row = tableData[i + 2];

    if (!line1Row || !line2Row || !line3Row) continue;

    // Use shared utility to merge 3 lines into 1-line format
    const oneLineRow = merge3LinesIntoRow(
      line1Row as unknown[],
      line2Row as unknown[],
      line3Row as unknown[]
    );

    result.push(oneLineRow);
  }

  return result;
};

/**
 * Convert 3-line table data format back to plans array
 * Converts enum labels to enum numbers for submission
 * Uses convert3LineTo1LineTableDataPreserveString to preserve string values during conversion,
 * then convertFromTableData to convert labels to enum numbers
 */
export const convertFrom3LineTableData = (
  tableData: unknown[][],
  plantOptions: SearchOption[] = [],
  witnessCompanyOptions: SearchOption[] = []
): CreatePlanRequest[] => {
  // Convert 3-line format to 1-line format first (preserve strings to avoid NaN)
  const oneLineData = convert3LineTo1LineTableDataPreserveString(
    tableData,
    plantOptions,
    witnessCompanyOptions
  );

  // Then convert 1-line format to plans (convert labels to enum numbers)
  return convertFromTableData(oneLineData, plantOptions, witnessCompanyOptions);
};
